/**
 * Wie useQuery, aber mit Kopie im Gerät: Ohne Netz erscheint der zuletzt
 * geladene Stand. Für Daten, die im Laden gebraucht werden (Listen, Läden,
 * Vorräte).
 */
import { useCallback, useEffect, useState } from 'react';
import { ApiError, api } from './client.ts';

/** Ergebnis eines Ladevorgangs mit Gerätekopie. */
export interface CachedQueryResult<T> {
  readonly data: T | null;
  readonly loading: boolean;
  readonly error: string | null;
  /** Die Daten stammen aus dem Gerät, weil der Server nicht erreichbar war. */
  readonly offline: boolean;
  reload: () => void;
  /** Ersetzt die Daten, zum Beispiel mit der Antwort einer Änderung. */
  setData: (data: T) => void;
}

/** Liest die Gerätekopie. */
function readCache<T>(key: string): T | null {
  try {
    const raw = window.localStorage.getItem(key);
    return raw === null ? null : (JSON.parse(raw) as T);
  } catch {
    return null;
  }
}

/** Schreibt die Gerätekopie. */
function writeCache(key: string, value: unknown): void {
  try {
    window.localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // Speicher voll: dann eben ohne Kopie.
  }
}

/**
 * Lädt Daten und hält eine Kopie im Gerät.
 * @param path Pfad der Schnittstelle.
 * @param cacheKey Name der Gerätekopie.
 */
export function useCachedQuery<T>(path: string, cacheKey: string): CachedQueryResult<T> {
  const storageKey = `kochbuch.${cacheKey}`;
  const [data, setState] = useState<T | null>(() => readCache<T>(storageKey));
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [offline, setOffline] = useState(false);
  const [counter, setCounter] = useState(0);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    api
      .get<T>(path)
      .then((result) => {
        if (alive) {
          setState(result);
          writeCache(storageKey, result);
          setOffline(false);
          setError(null);
          setLoading(false);
        }
      })
      .catch((caught: unknown) => {
        if (alive) {
          const noConnection = caught instanceof ApiError && caught.status === 0;
          setOffline(noConnection);
          setError(noConnection ? null : caught instanceof Error ? caught.message : 'Unbekannter Fehler.');
          setLoading(false);
        }
      });
    return () => {
      alive = false;
    };
  }, [path, storageKey, counter]);

  const reload = useCallback(() => setCounter((value) => value + 1), []);
  const setData = useCallback(
    (value: T) => {
      setState(value);
      writeCache(storageKey, value);
    },
    [storageKey],
  );

  return { data, loading, error, offline, reload, setData };
}
